import { getMousePos } from "./mouse";
import { pan } from "./pan";
import { zoom } from "./zoom";
import type { State } from "./types";
import {
  deleteSelectedDrawing,
  getDrawingCursor,
  handleDrawingMouseDown,
  handleDrawingMouseMove,
  handleDrawingMouseUp,
  startEditingSelectedTextDrawing,
} from "./drawingTools";

type SetupChartEventsArgs = {
  canvas: HTMLCanvasElement;
  container: HTMLElement;
  ctx: CanvasRenderingContext2D;
  dpr: number;
  state: State;
  redraw: () => void;
  onVisibilityChange?: () => void;
};

type TouchGestureState = {
  mode: "idle" | "pan" | "pinch";
  primaryId: number | null;
  secondaryId: number | null;
  startX: number;
  startY: number;
  lastX: number;
  lastY: number;
  moved: boolean;
  pinchStartDistance: number;
};

// Wires mouse and wheel handlers for chart interactions.
export function setupChartEvents({
  canvas,
  container,
  state,
  redraw,
  onVisibilityChange,
}: SetupChartEventsArgs): void {
  let lastDragX = 0;
  const touchState: TouchGestureState = {
    mode: "idle",
    primaryId: null,
    secondaryId: null,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    moved: false,
    pinchStartDistance: 0,
  };
  const getPanCursor = () => (state.isDragging ? "grabbing" : "crosshair");
  const applyCursor = () => {
    canvas.style.cursor = getDrawingCursor(state) ?? getPanCursor();
  };
  canvas.style.touchAction = "none";

  const getTouchPos = (touch: Touch) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: touch.clientX - rect.left,
      y: touch.clientY - rect.top,
    };
  };

  const getTouchById = (touches: TouchList, id: number | null) => {
    if (id == null) return null;
    for (let i = 0; i < touches.length; i++) {
      const touch = touches.item(i);
      if (touch && touch.identifier === id) return touch;
    }
    return null;
  };

  const dispatchTap = (x: number, y: number) => {
    const rect = canvas.getBoundingClientRect();
    const clientX = rect.left + x;
    const clientY = rect.top + y;
    const options = {
      bubbles: true,
      cancelable: true,
      view: window,
      clientX,
      clientY,
      button: 0,
      buttons: 1,
    };
    canvas.dispatchEvent(new MouseEvent("mousedown", { ...options, detail: 1 }));
    canvas.dispatchEvent(new MouseEvent("mouseup", options));
    canvas.dispatchEvent(new MouseEvent("click", { ...options, detail: 1 }));
  };

  canvas.addEventListener("mousedown", (e) => {
    const pos = getMousePos(canvas, e);
    if (handleDrawingMouseDown(state, pos.x, pos.y, e.detail)) {
      state.isDragging = false;
      applyCursor();
      redraw();
      return;
    }

    state.isDragging = true;
    lastDragX = pos.x;
    applyCursor();
  });

  canvas.addEventListener("mousemove", (e) => {
    const pos = getMousePos(canvas, e);
    const prevCrosshairX = state.crosshairX;
    const prevCrosshairY = state.crosshairY;
    const prevCrosshairInside = state.crosshairInside;
    state.crosshairX = pos.x;
    state.crosshairY = pos.y;
    const plotLeft = state.left;
    const plotRight = state.width - state.right;
    const plotTop = state.top;
    const plotBottom = state.height - state.bottom;
    state.crosshairInside = pos.x >= plotLeft && pos.x <= plotRight && pos.y >= plotTop && pos.y <= plotBottom;

    if (handleDrawingMouseMove(state, pos.x, pos.y)) {
      applyCursor();
      redraw();
      return;
    }

    applyCursor();
    if (
      prevCrosshairX !== state.crosshairX ||
      prevCrosshairY !== state.crosshairY ||
      prevCrosshairInside !== state.crosshairInside
    ) {
      redraw();
    }

    if (!state.isDragging) return;
    pan(state, pos.x - lastDragX, redraw, onVisibilityChange);
    lastDragX = pos.x;
  });

  canvas.addEventListener("mouseleave", () => {
    state.crosshairInside = false;
    if (!state.isDragging) canvas.style.cursor = "default";
    redraw();
  });

  canvas.addEventListener("mouseenter", () => {
    applyCursor();
  });

  window.addEventListener("mouseup", () => {
    if (handleDrawingMouseUp(state)) {
      applyCursor();
      redraw();
    }
    state.isDragging = false;
    applyCursor();
  });

  canvas.addEventListener("wheel", (e) => {
    e.preventDefault();
    const pos = getMousePos(canvas, e);
    zoom(state, pos.x, pos.y, e.deltaY, redraw, onVisibilityChange);
  });

  canvas.addEventListener(
    "touchstart",
    (e) => {
      if (e.touches.length === 0) return;
      e.preventDefault();

      if (e.touches.length >= 2) {
        const first = e.touches.item(0);
        const second = e.touches.item(1);
        if (!first || !second) return;
        const a = getTouchPos(first);
        const b = getTouchPos(second);
        touchState.mode = "pinch";
        touchState.primaryId = first.identifier;
        touchState.secondaryId = second.identifier;
        touchState.moved = false;
        touchState.pinchStartDistance = Math.max(1, Math.hypot(b.x - a.x, b.y - a.y));
        state.isDragging = false;
        applyCursor();
        return;
      }

      const touch = e.touches.item(0);
      if (!touch) return;
      const pos = getTouchPos(touch);
      touchState.mode = "pan";
      touchState.primaryId = touch.identifier;
      touchState.secondaryId = null;
      touchState.startX = pos.x;
      touchState.startY = pos.y;
      touchState.lastX = pos.x;
      touchState.lastY = pos.y;
      touchState.moved = false;
      state.crosshairX = pos.x;
      state.crosshairY = pos.y;
      state.crosshairInside =
        pos.x >= state.left &&
        pos.x <= state.width - state.right &&
        pos.y >= state.top &&
        pos.y <= state.height - state.bottom;
      applyCursor();
      redraw();
    },
    { passive: false },
  );

  canvas.addEventListener(
    "touchmove",
    (e) => {
      if (touchState.mode === "idle") return;
      e.preventDefault();

      if (touchState.mode === "pinch") {
        const first = getTouchById(e.touches, touchState.primaryId);
        const second = getTouchById(e.touches, touchState.secondaryId);
        if (!first || !second) return;
        const a = getTouchPos(first);
        const b = getTouchPos(second);
        const currentDistance = Math.max(1, Math.hypot(b.x - a.x, b.y - a.y));
        const centerX = (a.x + b.x) / 2;
        const centerY = (a.y + b.y) / 2;
        const delta = 100 * Math.log(touchState.pinchStartDistance / currentDistance);
        if (Math.abs(delta) < 0.1) return;
        touchState.moved = true;
        state.isDragging = false;
        state.zoomLastTime = 0;
        applyCursor();
        zoom(state, centerX, centerY, delta, redraw, onVisibilityChange);
        touchState.pinchStartDistance = currentDistance;
        return;
      }

      const touch = getTouchById(e.touches, touchState.primaryId) ?? e.touches.item(0);
      if (!touch) return;
      const pos = getTouchPos(touch);
      const dx = pos.x - touchState.lastX;
      const dy = pos.y - touchState.lastY;
      if (!touchState.moved && Math.hypot(pos.x - touchState.startX, pos.y - touchState.startY) < 4) {
        return;
      }

      touchState.moved = true;
      touchState.lastX = pos.x;
      touchState.lastY = pos.y;
      state.isDragging = true;
      applyCursor();
      pan(state, dx, redraw, onVisibilityChange);
      if (Math.abs(dy) > Math.abs(dx) * 1.5) {
        state.crosshairInside = false;
      }
    },
    { passive: false },
  );

  canvas.addEventListener(
    "touchend",
    (e) => {
      if (touchState.mode === "idle") return;
      e.preventDefault();
      const shouldTap = touchState.mode === "pan" && !touchState.moved;
      const touch = getTouchById(e.changedTouches, touchState.primaryId);
      touchState.mode = "idle";
      touchState.primaryId = null;
      touchState.secondaryId = null;
      state.isDragging = false;
      applyCursor();
      if (shouldTap && touch) {
        const pos = getTouchPos(touch);
        dispatchTap(pos.x, pos.y);
      }
      redraw();
    },
    { passive: false },
  );

  canvas.addEventListener(
    "touchcancel",
    () => {
      touchState.mode = "idle";
      touchState.primaryId = null;
      touchState.secondaryId = null;
      state.isDragging = false;
      applyCursor();
      redraw();
    },
    { passive: false },
  );

  canvas.addEventListener("dblclick", (e) => {
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
    if (!startEditingSelectedTextDrawing(state, container, redraw)) return;
    e.preventDefault();
    state.isDragging = false;
    canvas.style.cursor = getDrawingCursor(state) ?? "default";
  });

  window.addEventListener("keydown", (e) => {
    if (e.key !== "Delete" && e.key !== "Backspace") return;
    const target = e.target as HTMLElement | null;
    if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
    if (!deleteSelectedDrawing(state)) return;
    e.preventDefault();
    applyCursor();
    redraw();
  });

  applyCursor();
}
